/**
 * Reject one learning moment on an attempt, or restore it.
 *
 * Shared by the admin analysis page and the teacher's attempt view — the same
 * action, the same endpoint, so the same control. The host supplies `onSubmit`
 * (its own API client) and refreshes its data after.
 *
 * Only a REJECTED moment offers Restore. A moment withdrawn because its
 * analysis was disputed or superseded offers nothing here: its mark belongs to
 * the analysis, and is changed from there.
 *
 * See docs/FeatureDesigns/learning-moments/moment-rejection-target.md.
 */
import { useState } from 'react';
import type { EncounterWithdrawn } from './types';
import type { AdjustmentWhyValue } from '../components/AdjustmentWhyFields';
import './LearningMoments.css';

interface Props {
  withdrawn?: EncounterWithdrawn;
  /** Reject (`true`, with an optional comment and why) or restore (`false`). */
  onSubmit: (rejected: boolean, comment?: string, why?: AdjustmentWhyValue) => Promise<unknown>;
}

/** Why a moment is wrong — the same two reasons as rejecting a diagnosis
 *  (TeacherAttemptView's REJECT_WHY), worded for a moment. Logged, not shown. */
export const MOMENT_REJECT_WHY: Array<{ label: string; why: AdjustmentWhyValue }> = [
  { label: 'We misread the work', why: { origin: 'system', reasonCode: 'misread' } },
  { label: 'Not what the student did', why: { origin: 'system', reasonCode: 'wrong_diagnosis' } },
];

export function MomentRejectControl({ withdrawn, onSubmit }: Props) {
  const [composing, setComposing] = useState(false);
  const [comment, setComment] = useState('');
  const [why, setWhy] = useState<AdjustmentWhyValue | undefined>();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Withdrawn with the analysis — nothing to do from a single moment.
  if (withdrawn && withdrawn.reason !== 'rejected') return null;

  const submit = async (rejected: boolean) => {
    setSaving(true);
    setError(null);
    try {
      if (rejected) await onSubmit(true, comment.trim() || undefined, why);
      else await onSubmit(false);
      setComposing(false);
      setComment('');
      setWhy(undefined);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  if (withdrawn) {
    return (
      <span className="mrc">
        <button type="button" className="mrc-btn mrc-btn--restore" disabled={saving} onClick={() => submit(false)}>
          <span aria-hidden="true">↺ </span>{saving ? 'Restoring…' : 'Restore'}
        </button>
        {error && <span className="mrc-error">{error}</span>}
      </span>
    );
  }

  if (!composing) {
    return (
      <span className="mrc">
        <button
          type="button"
          className="mrc-btn mrc-btn--reject"
          title="Reject this learning moment — the analysis and the mark are unchanged"
          onClick={() => setComposing(true)}
        >
          <span aria-hidden="true">✕ </span>Reject
        </button>
      </span>
    );
  }

  return (
    <div className="mrc mrc--composing">
      {MOMENT_REJECT_WHY.map(r => (
        <button key={r.label} type="button" className={`mrc-chip${why?.reasonCode === r.why.reasonCode ? ' on' : ''}`}
          aria-pressed={why?.reasonCode === r.why.reasonCode} disabled={saving}
          onClick={() => setWhy(w => (w?.reasonCode === r.why.reasonCode ? undefined : r.why))}>{r.label}</button>
      ))}
      <input
        className="mrc-input"
        type="text"
        value={comment}
        maxLength={2000}
        placeholder="Why is this moment wrong? (optional)"
        onChange={e => setComment(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter' && !saving) submit(true); }}
        autoFocus
      />
      <button type="button" className="mrc-btn mrc-btn--primary" disabled={saving} onClick={() => submit(true)}>
        {saving ? 'Rejecting…' : 'Reject moment'}
      </button>
      <button type="button" className="mrc-btn" disabled={saving} onClick={() => { setComposing(false); setWhy(undefined); setError(null); }}>
        Cancel
      </button>
      {error && <span className="mrc-error">{error}</span>}
    </div>
  );
}

/** "Rejected by Ms Rao — '(a)' was read as 'rad'" — the line shown under a
 *  rejected moment. `byName` when the host resolved one. */
export function rejectionNote(w: EncounterWithdrawn & { byName?: string }): string {
  const who = w.byName ? ` by ${w.byName}` : '';
  const when = ` on ${new Date(w.at).toLocaleDateString()}`;
  return `Rejected${who}${when}${w.comment ? ` — ${w.comment}` : ''}`;
}
